import React, { useState } from 'react';
import { Plus, X, Eye } from 'lucide-react';
import { VisibilityRule } from '../../../services/queries/buttonActionQueries';

interface VisibilityRulesBuilderProps {
  rules: VisibilityRule[];
  logic: 'AND' | 'OR';
  availableFields: any[];
  onChange: (rules: VisibilityRule[], logic: 'AND' | 'OR') => void;
}

export const VisibilityRulesBuilder: React.FC<VisibilityRulesBuilderProps> = ({
  rules,
  logic,
  availableFields,
  onChange
}) => {
  const [editingRule, setEditingRule] = useState<string | null>(null);

  const handleAddRule = () => {
    const firstField = availableFields[0];
    if (!firstField) return;

    const newRule: VisibilityRule = {
      id: `vis_${Date.now()}`,
      fieldId: firstField.id,
      fieldLabel: firstField.label,
      operator: 'equals',
      value: ''
    };

    onChange([...rules, newRule], logic);
    setEditingRule(newRule.id);
  };

  const handleUpdateRule = (ruleId: string, updates: Partial<VisibilityRule>) => {
    onChange(
      rules.map(r => {
        if (r.id === ruleId) {
          // If field changed, reset value
          if (updates.fieldId) {
            const field = availableFields.find(f => f.id === updates.fieldId);
            if (field) {
              return {
                ...r,
                fieldId: field.id,
                fieldLabel: field.label,
                value: ''
              };
            }
          }
          return { ...r, ...updates };
        }
        return r;
      }),
      logic
    );
  };

  const handleRemoveRule = (ruleId: string) => {
    onChange(
      rules.filter(r => r.id !== ruleId),
      logic
    );
  };

  const handleChangeLogic = (newLogic: 'AND' | 'OR') => {
    onChange(rules, newLogic);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Eye className="w-5 h-5 text-gray-400" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Visibility Rules ({rules.length})
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Show button only when these conditions are met
            </p>
          </div>
        </div>
        <button
          onClick={handleAddRule}
          className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Add Rule
        </button>
      </div>

      {rules.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Logic:</span>
          <div className="flex gap-2">
            <button
              onClick={() => handleChangeLogic('AND')}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                logic === 'AND'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              AND (All must be true)
            </button>
            <button
              onClick={() => handleChangeLogic('OR')}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                logic === 'OR'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              OR (Any can be true)
            </button>
          </div>
        </div>
      )}

      {rules.length > 0 ? (
        <div className="space-y-3">
          {rules.map((rule, index) => (
            <VisibilityRuleRow
              key={rule.id}
              rule={rule}
              availableFields={availableFields}
              isEditing={editingRule === rule.id}
              onEdit={() => setEditingRule(rule.id)}
              onUpdate={(updates) => handleUpdateRule(rule.id, updates)}
              onRemove={() => handleRemoveRule(rule.id)}
              showLogic={index > 0}
              logic={logic}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-6 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
          <Eye className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No visibility rules</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Button will always be visible. Add rules to control visibility.
          </p>
        </div>
      )}
    </div>
  );
};

// Visibility Rule Row Component
interface VisibilityRuleRowProps {
  rule: VisibilityRule;
  availableFields: any[];
  isEditing: boolean;
  onEdit: () => void;
  onUpdate: (updates: Partial<VisibilityRule>) => void;
  onRemove: () => void;
  showLogic: boolean;
  logic: 'AND' | 'OR';
}

const VisibilityRuleRow: React.FC<VisibilityRuleRowProps> = ({
  rule,
  availableFields,
  isEditing,
  onEdit,
  onUpdate,
  onRemove,
  showLogic,
  logic
}) => {
  const selectedField = availableFields.find(f => f.id === rule.fieldId);
  const needsValue = !['isBlank', 'isNotBlank'].includes(rule.operator);

  const operators = [
    { value: 'equals', label: 'equals' },
    { value: 'notEquals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'isBlank', label: 'is blank' },
    { value: 'isNotBlank', label: 'is not blank' }
  ];

  return (
    <div className="space-y-2">
      {showLogic && (
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className={`text-xs font-bold px-3 py-1 rounded ${
            logic === 'AND'
              ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
              : 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
          }`}>
            {logic}
          </span>
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>
      )}

      <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
        {isEditing ? (
          <>
            {/* Field Selector */}
            <div className="flex-1">
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Field</label>
              <select
                value={rule.fieldId}
                onChange={(e) => onUpdate({ fieldId: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {availableFields.map(field => (
                  <option key={field.id} value={field.id}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Operator Selector */}
            <div className="flex-1">
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Operator</label>
              <select
                value={rule.operator}
                onChange={(e) => onUpdate({ operator: e.target.value as any })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {operators.map(op => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Value Input */}
            {needsValue && (
              <div className="flex-1">
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Value</label>
                {selectedField?.type === 'checkbox' ? (
                  <select
                    value={String(rule.value)}
                    onChange={(e) => onUpdate({ value: e.target.value === 'true' })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="true">True</option>
                    <option value="false">False</option>
                  </select>
                ) : selectedField?.type === 'picklist' ? (
                  <select
                    value={String(rule.value)}
                    onChange={(e) => onUpdate({ value: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select value...</option>
                    {selectedField.options?.map((opt: any) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={selectedField?.type === 'number' || selectedField?.type === 'currency' ? 'number' : 'text'}
                    value={String(rule.value)}
                    onChange={(e) => onUpdate({ value: e.target.value })}
                    placeholder="Enter value..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                )}
              </div>
            )}

            {/* Remove Button */}
            <div className={needsValue ? 'pt-6' : 'pt-6'}>
              <button
                onClick={onRemove}
                className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                title="Remove rule"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Display Mode */}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-900 dark:text-white">
                <span className="font-medium">{rule.fieldLabel}</span>
                {' '}
                <span className="text-purple-600 dark:text-purple-400 font-medium">
                  {operators.find(o => o.value === rule.operator)?.label}
                </span>
                {needsValue && (
                  <>
                    {' '}
                    <span className="font-semibold">
                      {selectedField?.type === 'checkbox'
                        ? (rule.value ? 'True' : 'False')
                        : selectedField?.type === 'currency'
                        ? `${Number(rule.value).toLocaleString()}`
                        : String(rule.value) || '(empty)'}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Edit/Remove Buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={onEdit}
                className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                title="Edit rule"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
              <button
                onClick={onRemove}
                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                title="Remove rule"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
